/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef } from 'react'
import { cloneDeep, extend } from 'lodash'
import { Button } from 'react-bootstrap'
import { useServiceContext } from '../../../../root'
import { useModal } from '../../../../contexts/ModalContext'
import Modal from '../../../../components/Modal'
import Loader from '../../../../components/shared/Loader'
import { useEsignature } from '../../../../hooks/useEsignature'
import RcmEsignatureModal from '../../../../components/esignature/RcmEsignatureModal'
import type { EosMissingSitesModalProps } from '../../../../types/eos'
import type { EosMissingSite } from '../../../../types/eos'

const MissingSitesModal: React.FC<EosMissingSitesModalProps> = ({
  params,
  isUserSponsorInstructionCoordinator,
  isInstructionPresent,
  isStudyArchived,
  isReverseNavigated
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const { showModal, hideModal } = useModal()
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()
  const [isOpen, setIsOpen] = useState(true)
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [sitesPresent, setSitesPresent] = useState(0)
  const [missingSiteNames, setMissingSiteNames] = useState<EosMissingSite[]>([])
  const [isAnySiteRenamed, setIsAnySiteRenamed] = useState(false)
  const [renameStatusModel, setRenameStatusModel] = useState<Record<number | string, boolean>>({})
  const [renameTextModel, setRenameTextModel] = useState<Record<number | string, string>>({})
  const [config, setConfig] = useState<any>({})
  const [options, setOptions] = useState<any>({})
  // Ref to track isAnySiteRenamed for auto-proceed (avoids stale closure)
  const isAnySiteRenamedRef = useRef(false)

  useEffect(() => {
    let tempOptions: any
    let tempConfig: any

    if (
      !params ||
      !params.options ||
      context.service.EosSetUpStep2DataService!.getChecksum(params.config as any) !== (params.config as any).checksum
    ) {
      // reinit config & options — outside changes detected
      tempOptions = extend(params.options, {
        sitesMap: {},
        countriesMap: {},
        studyOwners: [],
        availableUsers: [],
        activeWfs: null
      })
      setOptions(tempOptions)

      tempConfig = extend(params.config, {
        workflowInitiator: null,
        workflowValidation: null,
        createNewWorkflows: false,
        studyOwner: null,
        studyOwnerDetailed: null
      })

      context.service.EosSetUpStep2DataService!.loadData(tempConfig, tempOptions).then(() => {
        setConfig(tempConfig)
        setOptions(tempOptions)
        generateMissingSitesData(tempConfig, tempOptions)
      })
    } else {
      // keep config & options — no changes
      tempConfig = params?.config
      tempOptions = params?.options
      setConfig(tempConfig)
      setOptions(tempOptions)

      if ((params.config as any).refreshOptionsOnly) {
        ;(params.config as any).refreshOptionsOnly = null
        context.service.EosSetUpStep2DataService!.refreshData(tempConfig, tempOptions).then(
          () => {
            setOptions((prev: any) => ({ ...prev, sitesMap: { ...tempOptions?.sitesMap } }))
            generateMissingSitesData(tempConfig, tempOptions)
          },
          () => {
            setOptions((prev: any) => ({ ...prev, sitesMap: { ...tempOptions?.sitesMap } }))
            generateMissingSitesData(tempConfig, tempOptions)
          }
        )
      } else {
        generateMissingSitesData(tempConfig, tempOptions)
      }
    }
  }, [])

  const generateMissingSitesData = (configData: any, optionsData: any) => {
    context.api.eos
      .getSitesUnderStudy(configData?.folder?.id, null)
      .then((data: any[]) => {
        setIsDataLoading(false)
        setSitesPresent(data.length)

        const listedSites = Object.values(optionsData?.sitesMap ?? {}).filter((element: any) => element.isSite)

        const missingSitesData: EosMissingSite[] = []
        data.forEach((element: any) => {
          if ((listedSites as any[]).filter((site: any) => element.siteFolderId === site.rcmFolderId).length === 0) {
            missingSitesData.push(element)
            setRenameStatusModel((current) => ({
              ...current,
              [element.siteFolderId]: false
            }))
            setRenameTextModel((current) => ({
              ...current,
              [element.siteFolderId]: element.sanitizedSiteName
            }))
          }
        })

        setMissingSiteNames(missingSitesData)

        // Auto-proceed if no missing sites
        if (!missingSitesData.length) {
          doClose()
          if (isReverseNavigated) {
            import('./EosSetUpStep1Modal').then(({ default: EosSetUpStep1Modal }) => {
              showModal(<EosSetUpStep1Modal params={{ config: configData, options: optionsData }} />)
            })
          } else {
            const configClone = cloneDeep(configData)
            context.service.EosSetUpStep2DataService!.setChecksum(configClone)
            import('../step2/EosSetUpStep2').then(({ default: EosSetUpStep2 }) => {
              showModal(
                <EosSetUpStep2
                  params={{
                    config: configClone,
                    options: optionsData,
                    isMismatchDetected: false,
                    isAnySiteRenamed: isAnySiteRenamedRef.current
                  }}
                  isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
                  isInstructionPresent={isInstructionPresent}
                  isStudyArchived={isStudyArchived}
                />
              )
            })
          }
        }
      })
      .catch(() => {
        context.service.toast.error('Error in fetching sites')
        setIsDataLoading(false)
      })
  }

  const getMissingSiteActionMenu = (siteFolderId: number | string) => {
    if ((config as any)?.selectedFolder?.id) {
      if (renameTextModel[siteFolderId] && renameTextModel[siteFolderId].length) {
        context.api.files.getDetails(Number(siteFolderId)).then((data: any) => {
          renameSite(data)
        })
      } else {
        context.service.toast.error('SITE_NAME_EMPTY_ERROR')
      }
    }
  }

  const renameSite = async (site: any) => {
    const doRenameCallback = async () => {
      if (site.actionFlags?.eosSiteAndCanBeRenamed) {
        try {
          await context.api.eos.updateSiteFolderName(site.id, renameTextModel[site.id])
          context.service.toast.success('BROWSER_RENAME_FOLDER_SUCCESS_MSG')
          setRenameStatusModel((current) => ({ ...current, [site.id]: true }))
          setIsAnySiteRenamed(true)
          isAnySiteRenamedRef.current = true
        } catch (error: any) {
          if (error?.data?.code === 417) {
            context.service.toast.error('UNDERLYINF_DATA_WERE_CHNGD_TRY_AGAIN')
          } else {
            context.service.toast.error(translate('ERROR_ERROR', { error: error?.data?.message }))
          }
        }
      }
    }

    if (context.service.auth.requireESignatureForFileCommands()) {
      try {
        await signAction('ADMIN_ACTION', 'ADMIN_ACTION_RENAME')
        await doRenameCallback()
      } catch {
        // E-signature cancelled by user — do nothing
      }
    } else {
      await doRenameCallback()
    }
  }

  const doClose = () => {
    setIsOpen(false)
  }

  const goToPreviousStep = () => {
    import('./EosSetUpStep1Modal').then(({ default: EosSetUpStep1Modal }) => {
      showModal(<EosSetUpStep1Modal params={{ config, options }} />)
    })
    doClose()
  }

  const goToNextStep = () => {
    const configData = cloneDeep(config)
    context.service.EosSetUpStep2DataService!.setChecksum(configData)
    const propsData = {
      config: configData,
      options,
      isMismatchDetected: true,
      isAnySiteRenamed
    }
    import('../step2/EosSetUpStep2').then(({ default: EosSetUpStep2 }) => {
      showModal(
        <EosSetUpStep2
          params={propsData}
          isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
          isInstructionPresent={isInstructionPresent}
          isStudyArchived={isStudyArchived}
        />
      )
    })
    doClose()
  }

  return (
    <>
      <Modal
        show={isOpen}
        handleClose={doClose}
        title={translate('EOS_SETUP_TITLE_USERS_WORKFLOWS')}
        size="lg"
        className="missing-sites-modal-wrapper"
      >
        <Modal.Body>
          <div className="missing-sites-data-container missing-sites-details">
            {isDataLoading ? (
              <div className="mcc-loader-container loading-state">
                <Loader blocking={true} />
                <div className="mcc-loader-text">{translate('MISSING_SITES_LOADING')}</div>
              </div>
            ) : (
              <div>
                <div className="missing-sites-count-wrapper sites-count">
                  {translate('MISMATCH_COUNT_TEXT', {
                    missingSitesCount: missingSiteNames.length,
                    presentSitesCount: sitesPresent
                  })}
                </div>
                <div>
                  <div className="missing-sites-text-container">{translate('MISMATCH_DETECTED_MESSAGE')}</div>
                  <div className="study-name-container">
                    <span className="site-name">{translate('TABLE_TH_STUDY_NAME')}:</span>
                    {' ' + (config as any)?.study?.name}
                  </div>
                  <table className="missing-sites-data-table md-form-group fs12" style={{ border: 'none' }}>
                    <tbody>
                      <tr className="heading-row sticky-header">
                        <th className="table-headers">{translate('EXISTING_SITE_NAME')}</th>
                        <th className="table-headers">{translate('NEW_SITE_NAME')}</th>
                        <th className="table-headers">{translate('REPORTS_TABLE_TH_ACTION')}</th>
                        <th className="table-headers">{translate('STATUS')}</th>
                      </tr>
                      {missingSiteNames.map((site, index) => (
                        <tr className="missing-site-name missing-site-row" key={index}>
                          <td className="site-name-table">{site.siteName}</td>
                          <td>
                            <input
                              type="text"
                              value={renameTextModel[site.siteFolderId] ?? ''}
                              className="md-input"
                              onChange={(e) => {
                                const newValue = e.target.value
                                setRenameTextModel((current) => ({
                                  ...current,
                                  [site.siteFolderId]: newValue
                                }))
                              }}
                              maxLength={255}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-default rename-button"
                              onClick={() => getMissingSiteActionMenu(site.siteFolderId)}
                            >
                              {translate('ADMIN_ACTION_RENAME')}
                            </button>
                          </td>
                          <td className="update-status-checkbox">
                            <div className={renameStatusModel[site.siteFolderId] ? '' : 'error'}>
                              {translate(renameStatusModel[site.siteFolderId] ? 'NAME_UPDATED' : 'NAME_MISMATCH')}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Button variant="link" onClick={doClose}>
            {translate('CANCEL')}
          </Modal.Footer.Button>
          <Button variant="light" className="btn btn-light text-capitalize" onClick={goToPreviousStep}>
            {translate('SEARCH_BACK')}
          </Button>
          <Button
            variant="primary"
            className="btn btn-primary text-capitalize"
            onClick={goToNextStep}
            disabled={isDataLoading}
          >
            {translate('ETMF_GENERATE_TRIAL_STRCT_BTN_NEXT')}
          </Button>
        </Modal.Footer>
      </Modal>
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}

export default MissingSitesModal
